import React from 'react'
import {
  Box,
  Grid,
  Divider,
  Typography,
  Tabs,
  Tab,
  Button,
  Stack,
  Chip,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ColorLensIcon from '@mui/icons-material/ColorLens'
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import LanguageIcon from '@mui/icons-material/Language'
import AnimationIcon from '@mui/icons-material/Animation'
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined'

import ConfigSection from '../components/ConfigSection'
import ConfigField from '../components/ConfigField'
import ConfigToggle from '../components/ConfigToggle'
import ConfigSelect from '../components/ConfigSelect'
import { AppearanceConfig } from '../types/EnvironmentConfig.types'
import {
  DEFAULT_SEVV_THEME_TOKENS,
  resolveEffectiveChrome,
  resolveEffectivePalette,
  type SevvGameInRoomSurfaces,
  type SevvThemeChrome,
  type SevvThemeTokens,
} from '@/theme/sevvThemeTokenModel'
import { UI_SURFACE_CATEGORIES } from '@/theme/uiSurfaceRegistry'
import { ColorPicker } from '../components/ColorPicker'
import { ShellModalChromePickers } from '../components/ShellModalChromePickers'
import {
  environmentConfigSectionDividerSx,
  environmentConfigSectionRootSx,
} from '../utils/environmentConfigChrome'
import ThemePreviewPanel from '../components/ThemePreviewPanel'

const PALETTE_FIELDS: { key: keyof SevvThemeTokens['palette']; label: string }[] = [
  { key: 'primary', label: 'Primary' },
  { key: 'primaryLight', label: 'Primary light' },
  { key: 'primaryDark', label: 'Primary dark' },
  { key: 'secondary', label: 'Secondary' },
  { key: 'success', label: 'Success' },
  { key: 'warning', label: 'Warning' },
  { key: 'error', label: 'Error' },
  { key: 'info', label: 'Info' },
  { key: 'backgroundDefault', label: 'Background (app)' },
  { key: 'backgroundPaper', label: 'Background (surfaces)' },
  { key: 'textPrimary', label: 'Text primary' },
  { key: 'textSecondary', label: 'Text secondary' },
  { key: 'divider', label: 'Divider' },
]

const GAME_IN_ROOM_SURFACE_COLOR_FIELDS: {
  key: keyof SevvGameInRoomSurfaces
  label: string
  helper: string
}[] = [
  {
    key: 'toolbarGradientStart',
    label: 'Toolbar gradient start',
    helper: 'Defaults to Background (surfaces) from the light palette.',
  },
  {
    key: 'toolbarGradientEnd',
    label: 'Toolbar gradient end',
    helper: 'Defaults to Primary pale from the light palette.',
  },
  {
    key: 'panelPaperBg',
    label: 'Panel / dialog paper',
    helper: 'Solid surfaces such as room info and performance monitor.',
  },
  {
    key: 'accentMuted',
    label: 'Muted accent',
    helper: 'Section labels and secondary emphasis inside game HUD menus.',
  },
]

const GAME_IN_ROOM_SURFACE_TEXT_FIELDS: {
  key: keyof SevvGameInRoomSurfaces
  label: string
  placeholder: string
  helper: string
}[] = [
  {
    key: 'toolbarBorder',
    label: 'Toolbar & menu border',
    placeholder: 'rgba(0,0,0,0.12) or #hex',
    helper: 'Optional; defaults to a primary-tinted border.',
  },
  {
    key: 'iconButtonBg',
    label: 'HUD icon button background',
    placeholder: 'rgba(...) or hex',
    helper: 'Toolbar / profile icon button fill.',
  },
  {
    key: 'iconButtonHoverBg',
    label: 'HUD icon button hover',
    placeholder: 'rgba(...) or hex',
    helper: 'Hover state for HUD icon buttons.',
  },
  {
    key: 'menuPaperBg',
    label: 'Menu / modal paper (CSS)',
    placeholder: 'linear-gradient(...) or #hex',
    helper: 'Full CSS background for menus and gradient modals; defaults to the toolbar gradient.',
  },
  {
    key: 'dialogTitleBackground',
    label: 'In-room dialog title background (CSS)',
    placeholder: 'linear-gradient(135deg, ...) or #hex',
    helper: 'Game guide and similar; inherits global dialog title bar when unset.',
  },
  {
    key: 'dialogTitleColor',
    label: 'In-room dialog title text (CSS color)',
    placeholder: '#ffffff',
    helper: 'Title and icon color on game modal headers.',
  },
  {
    key: 'dialogTitleBorderBottom',
    label: 'In-room dialog title border-bottom',
    placeholder: '1px solid rgba(...)',
    helper: 'Optional; inherits global dialog title border when unset.',
  },
]

interface AppearanceSettingsProps {
  config: AppearanceConfig
  onChange: (updates: Partial<AppearanceConfig>) => void
  designTokens: SevvThemeTokens
  onDesignTokensChange: (updates: Partial<SevvThemeTokens>) => void
  disabled?: boolean
}

/**
 * Appearance Settings Section
 * Manages theme mode, typography, animations, and localization settings
 */
const AppearanceSettings: React.FC<AppearanceSettingsProps> = ({
  config,
  onChange,
  designTokens,
  onDesignTokensChange,
  disabled = false,
}) => {
  const theme = useTheme()
  const [paletteToneTab, setPaletteToneTab] = React.useState<'light' | 'dark'>('light')
  const [shellToneTab, setShellToneTab] = React.useState<'light' | 'dark'>('light')
  const [appearanceScopeTab, setAppearanceScopeTab] = React.useState<'global' | string>('global')

  const patchPalette = (key: keyof SevvThemeTokens['palette'], value: string) => {
    onDesignTokensChange({
      palette: {
        ...designTokens.palette,
        [key]: value,
      },
    })
  }

  const patchDarkPalette = (key: keyof SevvThemeTokens['palette'], value: string | null) => {
    const next: Partial<SevvThemeTokens['palette']> = { ...designTokens.darkPalette }
    if (value === null) {
      delete next[key]
    } else {
      next[key] = value
    }
    onDesignTokensChange({ darkPalette: next })
  }

  const effectivePaletteLight = resolveEffectivePalette(designTokens, 'light')
  const effectivePaletteDark = resolveEffectivePalette(designTokens, 'dark')
  const effectiveChromeLight = resolveEffectiveChrome(designTokens, effectivePaletteLight, 'light')
  const effectiveChromeDark = resolveEffectiveChrome(designTokens, effectivePaletteDark, 'dark')

  const patchChrome = (partial: Partial<NonNullable<SevvThemeTokens['chrome']>>) => {
    onDesignTokensChange({
      chrome: {
        ...DEFAULT_SEVV_THEME_TOKENS.chrome,
        ...designTokens.chrome,
        ...partial,
      },
    })
  }

  const patchChromeDark = (partial: Partial<SevvThemeChrome>) => {
    const cd: Partial<SevvThemeChrome> = { ...(designTokens.chromeDark ?? {}) }
    ;(Object.keys(partial) as (keyof SevvThemeChrome)[]).forEach((k) => {
      const v = partial[k]
      if (v === undefined) {
        delete cd[k]
      } else {
        ;(cd as Record<string, string>)[k] = v
      }
    })
    onDesignTokensChange({ chromeDark: cd })
  }

  const resetChromeDark = () => onDesignTokensChange({ chromeDark: {} })

  const effLightPalette = resolveEffectivePalette(designTokens, 'light')
  const gameSurfaces = designTokens.surfaces?.gameInRoom ?? {}

  const patchGameInRoomSurface = (key: keyof SevvGameInRoomSurfaces, value: string | null) => {
    const next: Partial<SevvGameInRoomSurfaces> = { ...gameSurfaces }
    if (value === null || value.trim() === '') {
      delete next[key]
    } else {
      next[key] = value.trim()
    }
    onDesignTokensChange({
      surfaces: {
        ...designTokens.surfaces,
        gameInRoom: Object.keys(next).length ? next : {},
      },
    })
  }

  const gameSurfaceDisplayColor = (key: keyof SevvGameInRoomSurfaces, fallback: string) =>
    gameSurfaces[key]?.trim() || fallback

  const chromeDarkKeyCount = Object.keys(designTokens.chromeDark ?? {}).filter(
    (k) => designTokens.chromeDark![k as keyof SevvThemeChrome] !== undefined
  ).length

  const handleChange = (field: keyof AppearanceConfig, value: any) => {
    onChange({ [field]: value })
  }

  const handleNestedChange = (parentField: keyof AppearanceConfig, childField: string, value: any) => {
    const currentValue = config[parentField] as any
    onChange({
      [parentField]: {
        ...currentValue,
        [childField]: value,
      },
    })
  }

  const renderSurfaceScope = () => {
    const surfaceCategory = UI_SURFACE_CATEGORIES.find((c) => c.id === appearanceScopeTab)
    if (!surfaceCategory) {
      return <Typography color="text.secondary">Unknown surface category.</Typography>
    }
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          {surfaceCategory.description}
        </Typography>
        <ConfigSection
          title={`Surfaces: ${surfaceCategory.label}`}
          description="Registry of modals, menus, and panels (paths relative to client/src)."
          icon={<WidgetsOutlinedIcon />}
          defaultExpanded={true}
        >
          <Stack spacing={2} sx={{ mb: 2 }}>
            {surfaceCategory.members.map((m) => (
              <Box
                key={m.id}
                sx={{
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2">{m.title}</Typography>
                  <Chip size="small" label={m.type} variant="outlined" />
                </Stack>
                {m.primaryFiles.map((f) => (
                  <Typography key={f} variant="caption" component="div" sx={{ fontFamily: 'monospace', display: 'block' }}>
                    {f}
                  </Typography>
                ))}
              </Box>
            ))}
          </Stack>
          {surfaceCategory.id === 'gameInRoom' && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Optional overrides
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Unset values inherit from the resolved global palette for the active theme mode in-game.
              </Typography>
              <Grid container spacing={3}>
                {GAME_IN_ROOM_SURFACE_COLOR_FIELDS.map(({ key, label, helper }) => {
                  const fallback =
                    key === 'toolbarGradientStart'
                      ? effLightPalette.backgroundPaper
                      : key === 'toolbarGradientEnd'
                        ? effLightPalette.primaryPale
                        : key === 'panelPaperBg'
                          ? effLightPalette.backgroundPaper
                          : effLightPalette.textSecondary
                  const hasOverride = gameSurfaces[key] !== undefined && String(gameSurfaces[key]).trim() !== ''
                  return (
                    <Grid item xs={12} md={6} key={key}>
                      <Stack spacing={0.5}>
                        <ColorPicker
                          label={label}
                          value={gameSurfaceDisplayColor(key, fallback)}
                          onChange={(v) => patchGameInRoomSurface(key, v)}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {helper}
                        </Typography>
                        {hasOverride && (
                          <Button size="small" variant="text" disabled={disabled} onClick={() => patchGameInRoomSurface(key, null)}>
                            Use palette default
                          </Button>
                        )}
                      </Stack>
                    </Grid>
                  )
                })}
                {GAME_IN_ROOM_SURFACE_TEXT_FIELDS.map(({ key, label, placeholder, helper }) => {
                  const hasOverride = gameSurfaces[key] !== undefined && String(gameSurfaces[key]).trim() !== ''
                  return (
                    <Grid item xs={12} key={key}>
                      <Stack spacing={0.5}>
                        <ConfigField
                          label={label}
                          value={(gameSurfaces[key] as string | undefined) ?? ''}
                          onChange={(value) => patchGameInRoomSurface(key, value || null)}
                          type="text"
                          placeholder={placeholder}
                          disabled={disabled}
                          helpText={helper}
                        />
                        {hasOverride && (
                          <Button size="small" variant="text" disabled={disabled} onClick={() => patchGameInRoomSurface(key, null)}>
                            Clear override
                          </Button>
                        )}
                      </Stack>
                    </Grid>
                  )
                })}
              </Grid>
            </>
          )}
        </ConfigSection>
      </Box>
    )
  }

  const formColumn = (
    <>
      <ConfigSection
        title="Design system colors"
        description="Light palette is the base. Dark overrides apply only when Theme Mode is Dark or Auto with a dark system preference."
        icon={<ColorLensIcon />}
        defaultExpanded={true}
      >
        <Tabs
          value={paletteToneTab}
          onChange={(_, v) => setPaletteToneTab(v as 'light' | 'dark')}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Light palette" value="light" />
          <Tab label="Dark overrides" value="dark" />
        </Tabs>
        {paletteToneTab === 'light' && (
          <Grid container spacing={3}>
            {PALETTE_FIELDS.map(({ key, label }) => (
              <Grid item xs={12} md={6} key={key}>
                <ColorPicker label={label} value={designTokens.palette[key]} onChange={(v) => patchPalette(key, v)} />
              </Grid>
            ))}
          </Grid>
        )}
        {paletteToneTab === 'dark' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Unset rows inherit the light palette for that token. Use &quot;Use light value&quot; to remove a dark-only
              override.
            </Typography>
            <Grid container spacing={3}>
              {PALETTE_FIELDS.map(({ key, label }) => {
                const hasOverride = designTokens.darkPalette?.[key] !== undefined
                const effective = designTokens.darkPalette?.[key] ?? designTokens.palette[key]
                return (
                  <Grid item xs={12} md={6} key={key}>
                    <Stack spacing={0.5}>
                      <ColorPicker label={label} value={effective} onChange={(v) => patchDarkPalette(key, v)} />
                      {hasOverride && (
                        <Button size="small" variant="text" disabled={disabled} onClick={() => patchDarkPalette(key, null)}>
                          Use light value
                        </Button>
                      )}
                    </Stack>
                  </Grid>
                )
              })}
            </Grid>
          </>
        )}
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      <ConfigSection
        title="Shell & modals"
        description="Light shell is the base. Optional dark shell overrides apply in dark mode. Dialog paper (body) uses Background (surfaces) and Divider from the design tokens via theme and CSS variables—no raw CSS fields here."
        icon={<ViewQuiltIcon />}
        defaultExpanded={true}
      >
        <Tabs
          value={shellToneTab}
          onChange={(_, v) => setShellToneTab(v as 'light' | 'dark')}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Light shell" value="light" />
          <Tab label="Dark shell" value="dark" />
        </Tabs>
        {shellToneTab === 'light' && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ColorPicker
                label="Main menu sidebar background"
                value={effectiveChromeLight.shellSidebarBackground}
                onChange={(v) => patchChrome({ shellSidebarBackground: v })}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Default follows light &quot;Background (surfaces)&quot; when unset.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <ColorPicker
                label="Header / AppBar background"
                value={effectiveChromeLight.shellHeaderBackground}
                onChange={(v) => patchChrome({ shellHeaderBackground: v })}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Default follows primary brand color.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Modal overlay, dialog title bar, and footer (global)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Color and border controls only—no raw CSS. Targets MUI <code>DialogTitle</code> / <code>DialogActions</code>{' '}
                via theme and <code>--sevv-dialog-title-*</code> / <code>--sevv-dialog-actions-*</code>. Do not rely on
                Emotion <code>css-*</code> class hashes—they change each build.
              </Typography>
              <ShellModalChromePickers
                tone="light"
                designTokens={designTokens}
                effectivePalette={effectivePaletteLight}
                effectiveBackdrop={effectiveChromeLight.overlayBackdrop}
                effectiveTitleColor={effectiveChromeLight.dialogTitleColor}
                onPatch={(partial, t) => {
                  if (t === 'light') patchChrome(partial)
                }}
                hasDarkOverride={() => false}
                onClearDarkOverride={() => {}}
                disabled={disabled}
              />
            </Grid>
          </Grid>
        )}
        {shellToneTab === 'dark' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Overrides apply when the app is in dark mode. Defaults use the light shell plus merged dark palette for
              sidebar/paper fallbacks.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={disabled || chromeDarkKeyCount === 0}
              onClick={resetChromeDark}
              sx={{ mb: 2 }}
            >
              Reset all dark shell overrides
            </Button>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={0.5}>
                  <ColorPicker
                    label="Main menu sidebar background"
                    value={effectiveChromeDark.shellSidebarBackground}
                    onChange={(v) => patchChromeDark({ shellSidebarBackground: v })}
                  />
                  {designTokens.chromeDark?.shellSidebarBackground !== undefined && (
                    <Button
                      size="small"
                      variant="text"
                      disabled={disabled}
                      onClick={() => patchChromeDark({ shellSidebarBackground: undefined })}
                    >
                      Use light shell default
                    </Button>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={0.5}>
                  <ColorPicker
                    label="Header / AppBar background"
                    value={effectiveChromeDark.shellHeaderBackground}
                    onChange={(v) => patchChromeDark({ shellHeaderBackground: v })}
                  />
                  {designTokens.chromeDark?.shellHeaderBackground !== undefined && (
                    <Button
                      size="small"
                      variant="text"
                      disabled={disabled}
                      onClick={() => patchChromeDark({ shellHeaderBackground: undefined })}
                    >
                      Use light shell default
                    </Button>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  Modal overlay, dialog title bar, and footer (dark overrides)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Same pickers as light shell; values you set here override only in dark mode.
                </Typography>
                <ShellModalChromePickers
                  tone="dark"
                  designTokens={designTokens}
                  effectivePalette={effectivePaletteDark}
                  effectiveBackdrop={effectiveChromeDark.overlayBackdrop}
                  effectiveTitleColor={effectiveChromeDark.dialogTitleColor}
                  onPatch={(partial, t) => {
                    if (t === 'dark') patchChromeDark(partial)
                  }}
                  hasDarkOverride={(k) => designTokens.chromeDark?.[k] !== undefined}
                  onClearDarkOverride={(k) => patchChromeDark({ [k]: undefined } as Partial<SevvThemeChrome>)}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </>
        )}
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      <ConfigSection
        title="Theme Settings"
        description="Configure theme mode and appearance preferences"
        icon={<ColorLensIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigSelect
              label="Theme Mode"
              value={config.theme.mode}
              onChange={(value) => handleNestedChange('theme', 'mode', value)}
              options={[
                { value: 'light', label: 'Light Theme' },
                { value: 'dark', label: 'Dark Theme' },
                { value: 'auto', label: 'Match device (Auto)' },
              ]}
              disabled={disabled}
              helpText="Light or Dark applies to everyone. Match device follows each user's device light/dark setting."
            />
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      <ConfigSection
        title="Typography Settings"
        description="Configure fonts and text styling"
        icon={<TextFieldsIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Font Family"
              value={config.typography.fontFamily}
              onChange={(value) => handleNestedChange('typography', 'fontFamily', value)}
              type="text"
              placeholder="'Roboto', 'Helvetica', 'Arial', sans-serif"
              disabled={disabled}
              helpText="Primary font family for the platform"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ConfigSelect
              label="Font Size"
              value={config.typography.fontSize}
              onChange={(value) => handleNestedChange('typography', 'fontSize', value)}
              options={[
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
              ]}
              disabled={disabled}
              helpText="Base font size for the platform"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ConfigSelect
              label="Font Weight"
              value={config.typography.fontWeight}
              onChange={(value) => handleNestedChange('typography', 'fontWeight', value)}
              options={[
                { value: 'light', label: 'Light (300)' },
                { value: 'normal', label: 'Regular (400)' },
                { value: 'bold', label: 'Bold (700)' },
              ]}
              disabled={disabled}
              helpText="Default font weight for text"
            />
          </Grid>
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      <ConfigSection
        title="Animation Settings"
        description="Configure transitions and animation preferences"
        icon={<AnimationIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ConfigToggle
              label="Enable Animations"
              value={config.animations.enabled}
              onChange={(value) => handleNestedChange('animations', 'enabled', value)}
              description="Enable smooth transitions and animations throughout the platform"
              disabled={disabled}
            />
          </Grid>

          {config.animations.enabled && (
            <>
              <Grid item xs={12} md={6}>
                <ConfigField
                  label="Animation Duration (ms)"
                  value={config.animations.duration.toString()}
                  onChange={(value) => handleNestedChange('animations', 'duration', parseInt(value) || 300)}
                  type="number"
                  placeholder="300"
                  disabled={disabled}
                  helpText="Default animation duration in milliseconds"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ConfigSelect
                  label="Easing Function"
                  value={config.animations.easing}
                  onChange={(value) => handleNestedChange('animations', 'easing', value)}
                  options={[
                    { value: 'ease', label: 'Ease' },
                    { value: 'ease-in', label: 'Ease In' },
                    { value: 'ease-out', label: 'Ease Out' },
                    { value: 'ease-in-out', label: 'Ease In Out' },
                    { value: 'linear', label: 'Linear' },
                  ]}
                  disabled={disabled}
                  helpText="Easing function for animations"
                />
              </Grid>
            </>
          )}
        </Grid>
      </ConfigSection>

      <Divider sx={environmentConfigSectionDividerSx(theme)} />

      <ConfigSection
        title="Localization Settings"
        description="Configure language, timezone, and date/time formats"
        icon={<LanguageIcon />}
        defaultExpanded={true}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ConfigField
              label="Default Language"
              value={config.localization.language}
              onChange={(value) => handleNestedChange('localization', 'language', value)}
              type="text"
              placeholder="en"
              disabled={disabled}
              helpText="Default language code (e.g., en, es, fr)"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ConfigField
              label="Default Timezone"
              value={config.localization.timezone}
              onChange={(value) => handleNestedChange('localization', 'timezone', value)}
              type="text"
              placeholder="UTC"
              disabled={disabled}
              helpText="Default timezone (e.g., UTC, America/New_York)"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ConfigField
              label="Date Format"
              value={config.localization.dateFormat}
              onChange={(value) => handleNestedChange('localization', 'dateFormat', value)}
              type="text"
              placeholder="MM/DD/YYYY"
              disabled={disabled}
              helpText="Date format pattern (e.g., MM/DD/YYYY, DD/MM/YYYY)"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <ConfigSelect
              label="Time Format"
              value={config.localization.timeFormat}
              onChange={(value) => handleNestedChange('localization', 'timeFormat', value)}
              options={[
                { value: '12h', label: '12-Hour (AM/PM)' },
                { value: '24h', label: '24-Hour' },
              ]}
              disabled={disabled}
              helpText="Time format preference"
            />
          </Grid>
        </Grid>
      </ConfigSection>
    </>
  )

  const previewPanel = (
    <ThemePreviewPanel appearance={config} designTokens={designTokens} variant="full" />
  )

  return (
    <Box sx={environmentConfigSectionRootSx(theme)}>
      <Tabs
        value={appearanceScopeTab}
        onChange={(_, v) => setAppearanceScopeTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Global" value="global" />
        {UI_SURFACE_CATEGORIES.map((c) => (
          <Tab key={c.id} label={c.label} value={c.id} />
        ))}
      </Tabs>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8} sx={{ order: { xs: 2, md: 1 } }}>
          {appearanceScopeTab === 'global' ? formColumn : renderSurfaceScope()}
        </Grid>
        <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>
          <Box
            sx={{
              position: { md: 'sticky' },
              top: { md: 16 },
              alignSelf: 'flex-start',
            }}
          >
            {previewPanel}
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default AppearanceSettings
